/** @format */

import React, { FC, useState, useMemo, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Card } from 'kpc-react'
import DataSource from './DataSource'
import LinkAnalysis from './LinkAnalysis'
import Topology from './Topology'
import {
  HeaderFormValues,
  initHeaderFormValues,
  AnalysisFormValues,
  initAnalysisFormValues,
  AnalysisConfig,
  handelNodeLevel
} from './constants'
import { useAsyncFn, useAsync } from 'react-use'
import { request } from 'src/utils'
import Api from 'src/api/ApiCloud'
import lodash from 'lodash'

interface RdmaTopologyProps {
  className?: string
  sourceList: any[]
}
const RdmaTopology: FC<RdmaTopologyProps> = ({ className, sourceList }) => {
  // 数据源、设配筛选
  const [deviceValues, setDeviceValues] = useState<HeaderFormValues>(initHeaderFormValues)
  // 链路分析、展示名称
  const [analysisValues, setAnalysisValues] = useState<AnalysisFormValues>(initAnalysisFormValues)
  const [analysisConfig, setAnalysisConfig] = useState<AnalysisConfig[]>([])
  // 获取数据时间
  const [lastSyncAt, setLastSyncAt] = useState<string>('')

  useEffect(() => {
    const localHeaderValues = JSON.parse(localStorage?.getItem('ib_search') || '{}')
    setDeviceValues({
      ...deviceValues,
      dataSource: localHeaderValues?.dataSource || sourceList[0]?.ClusterID || '',
      device: localHeaderValues?.device || []
    })
  }, sourceList)

  // 修改数据源、设备
  const changeDeviecValues = (values: HeaderFormValues) => {
    setDeviceValues(values)
    localStorage.setItem('ib_search', JSON.stringify(values))
  }

  // 获取所有监控项
  const { value: metricList } = useAsync(async () => {
    const result = await request(Api.getUFMMetrics, {})
    return result?.data?.ListLinkMetricsResult?.Data || []
  }, [])

  // 获取所有设备
  const [{ value: allnode, loading: allnode_loading }, allnode_fetch] = useAsyncFn(async () => {
    if (deviceValues.dataSource) {
      const result = await request(Api.getUFMNodes, {
        data: { DataSource: deviceValues.dataSource }
      })
      return result?.data?.ListSystemsResult?.Data || []
    } else {
      return []
    }
  }, [deviceValues.dataSource])

  // 获取所有链路
  const [{ value: alledge, loading: alledge_loading }, alledge_fetch] = useAsyncFn(async () => {
    if (deviceValues.dataSource) {
      const result = await request(Api.getUFMEdges, {
        data: { DataSource: deviceValues.dataSource }
      })
      setLastSyncAt(result?.data?.ListLinksResult.LastSyncAt || '')
      const edges = []
      result?.data?.ListLinksResult?.Data?.forEach(item => {
        const { SourceGUID, DestinationGUID } = item
        const findIndex = edges.findIndex(edge => {
          return (
            lodash.difference(edge.edge_id.split('==='), [SourceGUID, DestinationGUID])?.length ===
            0
          )
        })
        if (findIndex >= 0) {
          edges[findIndex].links.push({ ...item })
        } else {
          edges.push({
            edge_id: `${SourceGUID}===${DestinationGUID}`,
            links: [{ ...item }]
          })
        }
      })
      return edges
    } else {
      return []
    }
  }, [deviceValues.dataSource])

  // 初始化请求所有设备跟链路
  useEffect(() => {
    updateGraph()
  }, [deviceValues.dataSource])

  // 设备、链路筛选
  const { nodelist, edgelist } = useMemo(() => {
    if (allnode?.length && alledge?.length) {
      // 设置设备都层级(y_level)
      const node_yLevel = handelNodeLevel(allnode, alledge)
      if (deviceValues.device?.length) {
        // 根据GUID查到该设备直连
        const getNodesEdges = GUID => {
          const edges = alledge.filter(({ edge_id }) => {
            const [SourceGUID, DestinationGUID] = edge_id.split('===')
            // 判断线是否重复
            const find = lodash.find(edge_filter, res => res.edge_id === edge_id)
            return !find && (SourceGUID === GUID || DestinationGUID === GUID)
          })
          const nodes = lodash.uniq(
            edges.reduce((result, { edge_id }) => {
              const [SourceGUID, DestinationGUID] = edge_id.split('===')
              return result.concat([SourceGUID, DestinationGUID])
            }, [])
          )
          return { nodes, edges }
        }

        // 查找出来的设备guid
        const nodefilterGuid = lodash.cloneDeep(deviceValues.device)
        // 查找跟设备相关联的线
        const edge_filter = []
        deviceValues.device.forEach(device => {
          const result = getNodesEdges(device)
          edge_filter.push(...result.edges)
          nodefilterGuid.push(...result.nodes)
          const find_item = lodash.find(allnode, res => res.GUID === device)
          if (find_item?.Type === 'host') {
            const swtich = result.edges.reduce((res, item) => {
              const [SourceGUID, DestinationGUID] = item.edge_id.split('===')
              !res.includes(SourceGUID) && SourceGUID !== device && res.push(SourceGUID)
              !res.includes(DestinationGUID) &&
                DestinationGUID !== device &&
                res.push(DestinationGUID)
              return res
            }, [])
            swtich.forEach(item => {
              const switch_result = getNodesEdges(item)
              const filter_node_host = switch_result.nodes.filter(item => {
                const find_item = lodash.find(allnode, res => res.GUID === item)
                return find_item?.Type === 'host'
              })
              nodefilterGuid.push(...filter_node_host)
              const filter_edge_host = switch_result.edges.filter(({ edge_id }) => {
                const [SourceGUID, DestinationGUID] = edge_id.split('===')
                return (
                  nodefilterGuid.includes(SourceGUID) && nodefilterGuid.includes(DestinationGUID)
                )
              })
              edge_filter.push(...filter_edge_host)
            })
          }
        }, [])

        const nodes = lodash.uniq(nodefilterGuid)
        const node_filter = node_yLevel.filter(({ GUID }) => {
          const find = nodes.find(item => item === GUID)
          return find
        })
        return {
          nodelist: node_filter,
          edgelist: edge_filter
        }
      } else {
        return {
          nodelist: node_yLevel,
          edgelist: alledge
        }
      }
    } else {
      return {
        nodelist: [],
        edgelist: []
      }
    }
  }, [deviceValues.device, allnode, alledge])

  // 初始化/刷新
  const updateGraph = () => {
    allnode_fetch()
    alledge_fetch()
  }

  // 设备筛选显示名称
  const deviceOptions = useMemo(() => {
    if (allnode?.length) {
      const options = allnode?.map(item => {
        return {
          value: item.GUID,
          label: item[analysisValues.showName]
        }
      })
      return options
    } else {
      setDeviceValues({
        ...deviceValues,
        device: []
      })
      return []
    }
  }, [analysisValues.showName, allnode])

  return (
    <Card className={className}>
      <LinkAnalysis
        values={analysisValues}
        updateGraph={updateGraph}
        setValues={setAnalysisValues}
        analysisConfig={analysisConfig}
        setAnalysisConfig={setAnalysisConfig}
        metricList={metricList}
        lastSyncAt={lastSyncAt}
      />
      <DataSource
        sourceOption={sourceList}
        deviceOptions={deviceOptions}
        allnode_loading={allnode_loading}
        formValues={deviceValues}
        setFormValues={changeDeviecValues}
      />
      <Topology
        updateGraph={updateGraph}
        nodelist={nodelist}
        edgelist={edgelist}
        dataSource={deviceValues.dataSource}
        searchNodes={deviceValues.device}
        layout={analysisValues.layout}
        analysisSwitch={analysisValues.analysisSwitch}
        showName={analysisValues.showName}
        analysisConfig={analysisConfig}
        metricList={metricList}
      />
    </Card>
  )
}

export default styled(RdmaTopology)``
